import { withTheme } from '@vebox/etui-system';
import '../utilities/format-ms.js';
import '../utilities/create-transition.js';
import { Component, createRef, createElement } from 'react';
import { c as _defineProperty, a as _objectSpread2, _ as _objectWithoutProperties, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import '@vebox/etui-system-elements';
import '@vebox/etui-colors';
import { ErrorLabel } from '../ErrorLabel/ErrorLabel.js';
import { FormItem } from '../FormItem/FormItem.js';
import Input from '../Input/Input.js';
import InputLabel from '../InputLabel/InputLabel.js';

const _excluded = ["label", "readOnly", "isDisabled", "autoComplete", "max", "min", "onMouseEnter", "onMouseLeave", "onMouseOver", "onMouseOut", "required", "onClick", "tabIndex", "type", "id", "inputMode", "pattern", "step", "maxLength", "autoFocus", "name", "onPaste", "onInput", "styles", "startInputAddon", "endInputAddon", "size", "testId", "error"];
const KeyCode = {
  BACKSPACE: 8,
  ENTER: 13,
  UP: 38,
  DOWN: 40,
  DELETE: 46
};
function noop() {}
function defaultParser(input) {
  return input.replace(/[^\w.-]+/g, '');
}

/**
 * When click and hold on a button - the speed of auto changin the value.
 */
const SPEED = 200;

/**
 * When click and hold on a button - the delay before auto changin the value.
 */
const DELAY = 600;

/**
 * Max Safe Integer -- on IE this is not available, so manually set the number in that case.
 * The reason this is used, instead of Infinity is because numbers above the MSI are unstable
 */
// eslint-disable-next-line no-restricted-properties
const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER || 2 ** 53 - 1;
const isValidProps = value => value !== undefined && value !== null;
const isValueDefined = value => typeof value !== 'undefined' && value !== null && value !== '';
function isNotCompleteNumber(num) {
  return isNaN(num) || num === '' || num === null || num && num.toString().indexOf('.') === num.toString().length - 1;
}
function getRatio(e) {
  let ratio = 1;
  if (e.metaKey || e.ctrlKey) {
    ratio = 0.1;
  } else if (e.shiftKey) {
    ratio = 10;
  }
  return ratio;
}
const isEqual = (oldValue, newValue) => newValue === oldValue || typeof newValue === 'number' && typeof oldValue === 'number' && isNaN(newValue) && isNaN(oldValue);
class NumberInput extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "input", /*#__PURE__*/createRef());
    _defineProperty(this, "onKeyDown", (e, ...args) => {
      const {
        onKeyDown,
        onPressEnter
      } = this.props;
      if (e.keyCode === KeyCode.UP) {
        const ratio = getRatio(e);
        this.up(e, ratio);
        this.stop();
      } else if (e.keyCode === KeyCode.DOWN) {
        const ratio = getRatio(e);
        this.down(e, ratio);
        this.stop();
      } else if (e.keyCode === KeyCode.ENTER && onPressEnter) {
        onPressEnter(e);
      }
      this.recordCursorPosition();
      this.lastKeyCode = e.keyCode;
      if (onKeyDown) {
        onKeyDown(e, ...args);
      }
    });
    _defineProperty(this, "onKeyUp", (e, ...args) => {
      const {
        onKeyUp
      } = this.props;
      this.stop();
      this.recordCursorPosition();
      if (onKeyUp) {
        onKeyUp(e, ...args);
      }
    });
    _defineProperty(this, "onChange", e => {
      const {
        onChange
      } = this.props;
      if (this.state.focused) {
        this.inputting = true;
      }
      this.rawInput = this.props.parser(this.getValueFromEvent(e));
      this.setState({
        inputValue: this.rawInput
      });
      onChange(this.toNumber(this.rawInput));
    });
    _defineProperty(this, "onMouseUp", (...args) => {
      const {
        onMouseUp
      } = this.props;
      this.recordCursorPosition();
      if (onMouseUp) {
        onMouseUp(...args);
      }
    });
    _defineProperty(this, "onFocus", (...args) => {
      this.setState({
        focused: true
      });
      this.props.onFocus(...args);
    });
    _defineProperty(this, "onBlur", (e, ...args) => {
      const {
        onBlur
      } = this.props;
      this.inputting = false;
      this.setState({
        focused: false
      });
      const value = this.getCurrentValidValue(this.state.inputValue);
      e.persist();
      const newValue = this.setValue(value);
      if (onBlur) {
        const originValue = this.input.current.value;
        const inputValue = this.getInputDisplayValue({
          focused: false,
          value: newValue
        });
        this.input.current.value = inputValue;
        onBlur(e, ...args);
        this.input.current.value = originValue;
      }
    });
    _defineProperty(this, "getRatio", e => {
      let ratio = 1;
      if (e.metaKey || e.ctrlKey) {
        ratio = 0.1;
      } else if (e.shiftKey) {
        ratio = 10;
      }
      return ratio;
    });
    _defineProperty(this, "getFullNum", num => {
      if (isNaN(num)) {
        return num;
      }
      if (!/e/i.test(String(num))) {
        return num;
      }
      return Number(num).toFixed(18).replace(/\.?0+$/, '');
    });
    _defineProperty(this, "getPrecision", value => {
      if (isValidProps(this.props.precision)) {
        return this.props.precision;
      }
      const valueString = String(value);
      if (valueString.indexOf('e-') >= 0) {
        return parseInt(valueString.slice(valueString.indexOf('e-') + 2), 10);
      }
      let precision = 0;
      if (valueString.indexOf('.') >= 0) {
        precision = valueString.length - valueString.indexOf('.') - 1;
      }
      return precision;
    });
    _defineProperty(this, "getInputDisplayValue", state => {
      const {
        focused,
        inputValue,
        value
      } = state || this.state;
      let inputDisplayValue;
      if (focused) {
        inputDisplayValue = inputValue;
      } else {
        inputDisplayValue = this.toPrecisionAsStep(value);
      }
      if (inputDisplayValue === undefined || inputDisplayValue === null) {
        inputDisplayValue = '';
      }
      let inputDisplayValueFormat = this.formatWrapper(inputDisplayValue);
      if (isValidProps(this.props.decimalSeparator)) {
        inputDisplayValueFormat = inputDisplayValueFormat.toString().replace('.', this.props.decimalSeparator);
      }
      return inputDisplayValueFormat;
    });
    _defineProperty(this, "getBorderColor", () => {
      const {
        theme,
        isDisabled,
        styles
      } = this.props;
      const {
        input
      } = styles;
      if (isDisabled) return theme.palette.action.disabledBackground;
      if (input && input.borderColor) {
        return input.borderColor;
      }
      return theme.palette.grey[60];
    });
    _defineProperty(this, "recordCursorPosition", () => {
      try {
        this.cursorStart = this.input.current.selectionStart;
        this.cursorEnd = this.input.current.selectionEnd;
        this.currentValue = this.input.current.value;
        this.cursorBefore = this.input.current.value.substring(0, this.cursorStart);
        this.cursorAfter = this.input.current.value.substring(this.cursorEnd);
      } catch (e) {
        // Fix error in Chrome:
        // Failed to read the 'selectionStart' property from 'HTMLInputElement'
        // http://stackoverflow.com/q/21177489/3040605
      }
    });
    _defineProperty(this, "restoreByAfter", str => {
      if (str === undefined) return false;
      const fullStr = this.input.current.value;
      const index = fullStr.lastIndexOf(str);
      if (index === -1) return false;
      const prevCursorPos = this.cursorBefore.length;
      if (this.lastKeyCode === KeyCode.DELETE && this.cursorBefore.charAt(prevCursorPos - 1) === str[0]) {
        this.fixCaret(prevCursorPos, prevCursorPos);
        return true;
      }
      if (index + str.length === fullStr.length) {
        this.fixCaret(index, index);
        return true;
      }
      return false;
    });
    _defineProperty(this, "partRestoreByAfter", str => {
      if (str === undefined) return false;
      return Array.prototype.some.call(str, (_, start) => {
        const partStr = str.substring(start);
        return this.restoreByAfter(partStr);
      });
    });
    _defineProperty(this, "stop", () => {
      if (this.autoStepTimer) {
        clearTimeout(this.autoStepTimer);
      }
    });
    _defineProperty(this, "down", (e, ratio, recursive) => {
      this.pressingUpOrDown = true;
      this.step('down', e, ratio, recursive);
    });
    _defineProperty(this, "up", (e, ratio, recursive) => {
      this.pressingUpOrDown = true;
      this.step('up', e, ratio, recursive);
    });
    let {
      value: _value
    } = props;
    if (_value === undefined) {
      _value = props.defaultValue;
    }
    this.state = {
      focused: props.autoFocus
    };
    const validValue = this.getValidValue(this.toNumber(_value));
    this.state = _objectSpread2(_objectSpread2({}, this.state), {}, {
      value: validValue,
      inputValue: this.toPrecisionAsStep(validValue)
    });
  }
  componentDidMount() {
    this.componentDidUpdate(null);
  }
  componentDidUpdate(prevProps) {
    const {
      value,
      onChange,
      max,
      min
    } = this.props;
    const {
      focused
    } = this.state;

    // Don't trigger in componentDidMount
    if (prevProps) {
      if (!isEqual(prevProps.value, value) || !isEqual(prevProps.max, max) || !isEqual(prevProps.min, min)) {
        const validValue = focused ? value : this.getValidValue(value);
        let nextInputValue;
        if (this.pressingUpOrDown) {
          nextInputValue = validValue;
        } else if (this.inputting) {
          nextInputValue = this.rawInput;
        } else {
          nextInputValue = this.toPrecisionAsStep(validValue);
        }
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          value: validValue,
          inputValue: nextInputValue
        });
      }
      const nextValue = 'value' in this.props ? value : this.state.value;
      if ('max' in this.props && prevProps.max !== max && typeof nextValue === 'number' && nextValue > max && onChange) {
        onChange(max);
      }
      if ('min' in this.props && prevProps.min !== min && typeof nextValue === 'number' && nextValue < min && onChange) {
        onChange(min);
      }
    }

    // Restore cursor
    try {
      // Firefox set the input cursor after it get focused.
      // This caused that if an input didn't init with the selection,
      // set will cause cursor not correct when first focus.
      // Safari will focus input if set selection. We need skip this.
      if (this.cursorStart !== undefined && this.state.focused) {
        // In most cases, the string after cursor is stable.
        // We can move the cursor before it

        if (
        // If not match full str, try to match part of str
        !this.partRestoreByAfter(this.cursorAfter) && this.state.value !== this.props.value) {
          // If not match any of then, let's just keep the position
          // TODO: Logic should not reach here, need check if happens
          let pos = this.cursorStart + 1;

          // If not have last string, just position to the end
          if (!this.cursorAfter) {
            pos = this.input.current.value.length;
          } else if (this.lastKeyCode === KeyCode.BACKSPACE) {
            pos = this.cursorStart - 1;
          } else if (this.lastKeyCode === KeyCode.DELETE) {
            pos = this.cursorStart;
          }
          this.fixCaret(pos, pos);
        } else if (this.currentValue === this.input.current.value) {
          // Handle some special key code
          switch (this.lastKeyCode) {
            case KeyCode.BACKSPACE:
              this.fixCaret(this.cursorStart - 1, this.cursorStart - 1);
              break;
            case KeyCode.DELETE:
              this.fixCaret(this.cursorStart + 1, this.cursorStart + 1);
              break;
            default:
            // Do nothing
          }
        }
      }
    } catch (e) {
      // Do nothing
    }

    // Reset last key
    this.lastKeyCode = null;

    // pressingUpOrDown is true means that someone just click up or down button
    if (!this.pressingUpOrDown) {
      return;
    }
    if (this.props.focusOnUpDown && this.state.focused) {
      if (document.activeElement !== this.input.current) {
        this.focus();
      }
    }
  }
  componentWillUnmount() {
    this.stop();
  }
  getCurrentValidValue(value) {
    let val = value;
    if (val === '') {
      val = '';
    } else if (!isNotCompleteNumber(parseFloat(val))) {
      val = this.getValidValue(val);
    } else {
      val = this.state.value;
    }
    return this.toNumber(val);
  }
  getValueFromEvent(e) {
    let value = e.target.value.trim().replace(/。/g, '.');
    if (isValidProps(this.props.decimalSeparator)) {
      value = value.replace(this.props.decimalSeparator, '.');
    }
    return value;
  }
  getValidValue(value, min = this.props.min, max = this.props.max) {
    let val = parseFloat(value);
    if (isNaN(val)) {
      return value;
    }
    if (val < min) {
      val = min;
    }
    if (val > max) {
      val = max;
    }
    return val;
  }
  setValue(v, callback) {
    const {
      precision
    } = this.props;
    const newValue = isNotCompleteNumber(parseFloat(v)) ? null : parseFloat(v);
    const {
      value = null
    } = this.state;
    let {
      inputValue = null
    } = this.state;
    const newValueInString = typeof newValue === 'number' ? newValue.toFixed(precision) : `${newValue}`;
    const changed = newValue !== value || newValueInString !== `${inputValue}`;
    if (!('value' in this.props)) {
      this.setState({
        value: newValue,
        inputValue: this.toPrecisionAsStep(v)
      }, callback);
    } else {
      // always set input value same as value
      inputValue = this.toPrecisionAsStep(this.state.value);
      this.setState({
        inputValue
      }, callback);
    }
    if (changed) {
      this.props.onChange(newValue);
    }
    return newValue;
  }
  getMaxPrecision(currentValue, ratio = 1) {
    const {
      precision,
      step
    } = this.props;
    if (isValidProps(precision)) {
      return precision;
    }
    const ratioPrecision = this.getPrecision(ratio);
    const stepPrecision = this.getPrecision(step);
    const currentValuePrecision = this.getPrecision(currentValue);
    if (!currentValue) {
      return ratioPrecision + stepPrecision;
    }
    return Math.max(currentValuePrecision, ratioPrecision + stepPrecision);
  }
  getPrecisionFactor(currentValue, ratio = 1) {
    const precision = this.getMaxPrecision(currentValue, ratio);
    // eslint-disable-next-line no-restricted-properties
    return 10 ** precision;
  }
  step(type, e, ratio = 1, recursive) {
    this.stop();
    if (e) {
      e.persist();
      e.preventDefault();
    }
    const {
      props
    } = this;
    if (props.isDisabled) {
      return;
    }
    const value = this.getCurrentValidValue(this.state.inputValue) || 0;
    if (isNotCompleteNumber(value)) {
      return;
    }
    let val = this[`${type}Step`](value, ratio);
    const outOfRange = val > props.max || val < props.min;
    if (val > props.max) {
      val = props.max;
    } else if (val < props.min) {
      val = props.min;
    }
    this.setValue(val, null);
    this.setState({
      focused: true
    }, () => {
      this.pressingUpOrDown = false;
    });
    if (outOfRange) {
      return;
    }
    this.autoStepTimer = setTimeout(() => {
      this[type](e, ratio, true);
    }, recursive ? SPEED : DELAY);
  }
  downStep(val, rat) {
    const {
      step
    } = this.props;
    const precisionFactor = this.getPrecisionFactor(val, rat);
    const precision = Math.abs(this.getMaxPrecision(val, rat));
    const result = ((precisionFactor * val - precisionFactor * +step * rat) / precisionFactor).toFixed(precision);
    return this.toNumber(result);
  }
  upStep(val, rat) {
    const {
      step
    } = this.props;
    const precisionFactor = this.getPrecisionFactor(val, rat);
    const precision = Math.abs(this.getMaxPrecision(val, rat));
    const result = ((precisionFactor * val + precisionFactor * +step * rat) / precisionFactor).toFixed(precision);
    return this.toNumber(result);
  }
  toNumber(num) {
    const {
      precision
    } = this.props;
    const {
      focused
    } = this.state;
    const numberIsTooLarge = num && num.length > 16 && focused;
    if (isNotCompleteNumber(num) || numberIsTooLarge) {
      return num;
    }
    if (isValidProps(precision)) {
      return (
        // eslint-disable-next-line no-restricted-properties
        Math.round(num * 10 ** precision) / 10 ** precision
      );
    }
    return Number(num);
  }
  toPrecisionAsStep(num) {
    if (isNotCompleteNumber(num) || num === '') {
      return num;
    }
    const precision = Math.abs(this.getMaxPrecision(num));
    if (!isNaN(precision)) {
      return Number(num).toFixed(precision);
    }
    return num.toString();
  }
  formatWrapper(num) {
    if (this.props.formatter) {
      return this.props.formatter(num);
    }
    return num;
  }
  blur() {
    this.input.current.blur();
  }
  focus() {
    this.input.current.focus();
    this.recordCursorPosition();
  }
  fixCaret(start, end) {
    if (start === undefined || end === undefined || !this.input || !this.input.current.value) {
      return;
    }
    try {
      const currentStart = this.input.current.selectionStart;
      const currentEnd = this.input.current.selectionEnd;
      if (start !== currentStart || end !== currentEnd) {
        this.input.current.setSelectionRange(start, end);
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }
  render() {
    const _this$props = this.props,
      {
        label,
        readOnly,
        isDisabled,
        autoComplete,
        max,
        min,
        onMouseEnter,
        onMouseLeave,
        onMouseOver,
        onMouseOut,
        required,
        onClick,
        tabIndex,
        type,
        id,
        inputMode,
        pattern,
        step,
        maxLength,
        autoFocus,
        name,
        onPaste,
        onInput,
        styles,
        startInputAddon,
        endInputAddon,
        size,
        testId,
        error
      } = _this$props,
      rest = _objectWithoutProperties(_this$props, _excluded);
    const {
      value,
      focused
    } = this.state;
    const dataOrAriaAttributeProps = {};
    Object.keys(rest).forEach(key => {
      if (key.substr(0, 5) === 'data-' || key.substr(0, 5) === 'aria-' || key === 'role') {
        dataOrAriaAttributeProps[key] = rest[key];
      }
    });
    const isEditable = !readOnly && !isDisabled;
    const inputDisplayValue = this.getInputDisplayValue(null);
    const hasValue = isValueDefined(inputDisplayValue);
    return /*#__PURE__*/createElement(FormItem, _extends({
      readOnly: readOnly
    }, styles.root), label && /*#__PURE__*/createElement(InputLabel, _extends({
      shouldFloat: hasValue || focused,
      htmlFor: name,
      disabled: isDisabled,
      size: size
    }, styles.label), label), startInputAddon, /*#__PURE__*/createElement(Input, _extends({
      id: name,
      name: name,
      label: label,
      required: required,
      type: type,
      tabIndex: tabIndex,
      "aria-valuemin": min,
      "aria-valuemax": max,
      "aria-valuenow": value,
      onPaste: onPaste,
      onClick: onClick,
      onMouseUp: this.onMouseUp,
      onKeyDown: isEditable ? this.onKeyDown : noop,
      onKeyUp: isEditable ? this.onKeyUp : noop,
      autoComplete: autoComplete,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      autoFocus: autoFocus,
      maxLength: maxLength,
      readOnly: readOnly,
      disabled: isDisabled,
      max: max,
      min: min,
      step: step,
      onChange: this.onChange,
      elRef: this.input,
      value: this.getFullNum(inputDisplayValue),
      pattern: pattern,
      inputMode: inputMode,
      onInput: onInput,
      borderColor: this.getBorderColor(),
      size: size,
      testId: testId
    }, dataOrAriaAttributeProps, styles.input, {
      error: error
    })), error && /*#__PURE__*/createElement(ErrorLabel, null, error.errorMessage), endInputAddon);
  }
}
_defineProperty(NumberInput, "defaultProps", {
  size: 'default',
  focusOnUpDown: true,
  min: -MAX_SAFE_INTEGER,
  step: 1,
  onChange: noop,
  onKeyDown: noop,
  onPressEnter: noop,
  onFocus: noop,
  onBlur: noop,
  parser: defaultParser,
  autoComplete: 'off',
  isDisabled: false,
  styles: {}
});
var NumberInput$1 = withTheme(NumberInput);

export default NumberInput$1;
